"use client";

import { cva } from "class-variance-authority";
import { Menu } from "lucide-react";
import {
  Children,
  type ElementType,
  type FC,
  Fragment,
  type HTMLAttributes,
  isValidElement,
  type MouseEvent,
  type ReactNode,
  useState,
} from "react";
import { Container } from "@/components/ui/container";
import { DrawerDialog } from "@/components/ui/drawer-dialog";
import { Link } from "@/components/ui/link";
import { useStableId } from "@/lib/use-stable-id";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import { HeaderContext, type HeaderContextValue, useHeader } from "./header.context";

export type HeaderLayout = Extract<Layout, "desktop" | "mobile">;

export interface HeaderProps extends HTMLAttributes<HTMLElement> {
  layout?: HeaderLayout;
  activeValue?: string;
  as?: ElementType;
  isOpenExternal?: boolean;
  setIsOpenExternal?: (open: boolean) => void;
}

const Root: FC<HeaderProps> = ({
  layout = "desktop",
  activeValue,
  as: Tag = "header",
  className,
  children,
  isOpenExternal,
  setIsOpenExternal,
  ...props
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);

  const setMenuOpen = (open: boolean) => {
    if (isOpenExternal === undefined) setIsOpenInternal(open);
    setIsOpenExternal?.(open);
  };

  const contextValue: HeaderContextValue = {
    layout,
    activeValue,
    placement: "bar",
    menuOpen: isOpenExternal ?? isOpenInternal,
    setMenuOpen,
  };

  return (
    <HeaderContext.Provider value={contextValue}>
      <Tag data-layout={layout} className={cn("bg-bg-dark", className)} {...props}>
        {children}
      </Tag>
    </HeaderContext.Provider>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

const Bar: FC<PartProps> = ({ className, children }) => (
  <Container as="div" className={cn("flex h-20 items-center justify-between gap-4", className)}>
    {children}
  </Container>
);

interface BrandProps extends PartProps {
  href: string;
  label: string;
}

const Brand: FC<BrandProps> = ({ href, label, className, children }) => (
  <Link
    href={href}
    aria-label={label}
    className={cn("self-start no-underline hover:no-underline", className)}
  >
    {children}
  </Link>
);

interface NavProps extends PartProps {
  label: string;
  action?: ReactNode;
  menuTitle?: string;
  menuFooter?: ReactNode;
}

/**
 * `desktop`: the pill with the items and `action`.
 * `mobile`: the items and `menuFooter` in a drawer, opened by `Header.MenuButton`.
 */
const Nav: FC<NavProps> = ({
  label,
  action,
  menuTitle = "Menu",
  menuFooter,
  className,
  children,
}) => {
  const ctx = useHeader();
  const { layout, menuOpen, setMenuOpen } = ctx;
  const menuTitleId = useStableId();

  if (layout === "mobile") {
    return (
      <DrawerDialog
        title={menuTitle}
        labelledBy={menuTitleId}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      >
        <nav aria-label={label} data-layout={layout} className={className}>
          <ul className="flex flex-col">
            <HeaderContext.Provider value={{ ...ctx, placement: "menu" }}>
              {children}
            </HeaderContext.Provider>
          </ul>
        </nav>
        {menuFooter && <div className="border-t border-border-subtle p-5">{menuFooter}</div>}
      </DrawerDialog>
    );
  }

  return (
    <nav aria-label={label} data-layout={layout} className={cn("ml-16", className)}>
      <ul className="flex items-center gap-1 rounded-full bg-surface py-2 pr-2 pl-3 shadow-md">
        {Children.toArray(children).map((child, index) => (
          <Fragment key={isValidElement(child) ? child.key : index}>
            {index > 0 && (
              <li aria-hidden className="px-1">
                <span className="block h-4 w-px bg-border-default" />
              </li>
            )}
            {child}
          </Fragment>
        ))}
        {action && <li className="ml-1">{action}</li>}
      </ul>
    </nav>
  );
};

const ITEM_VARIANTS = cva("no-underline hover:no-underline", {
  variants: {
    placement: {
      bar: "relative inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-body-sm",
      menu: "flex items-center gap-3 border-b border-border-subtle px-5 py-4 text-body-md font-medium transition-colors hover:bg-bg-secondary/60",
    },
    active: {
      true: "text-primary",
      false: "text-fg-strong",
    },
  },
  compoundVariants: [
    { placement: "bar", active: true, className: "font-semibold" },
    {
      placement: "bar",
      active: false,
      className: "font-medium transition-colors hover:text-primary",
    },
  ],
  defaultVariants: { active: false },
});

interface ItemProps extends PartProps {
  value?: string;
  href: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

const Item: FC<ItemProps> = ({ value, href, onClick, className, children }) => {
  const { activeValue, placement, setMenuOpen } = useHeader();
  const active = value !== undefined && value === activeValue;

  return (
    <li>
      <Link
        href={href}
        variant="secondary"
        aria-current={active ? "page" : undefined}
        data-active={active || undefined}
        className={cn(ITEM_VARIANTS({ placement, active }), className)}
        onClick={(e) => {
          if (placement === "menu") setMenuOpen(false);
          onClick?.(e);
        }}
      >
        {active && placement === "menu" && (
          <span aria-hidden className="h-2 w-2 rounded-full bg-status-online" />
        )}
        {children}
        {active && placement === "bar" && (
          <span
            aria-hidden
            className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-accent"
          />
        )}
      </Link>
    </li>
  );
};

const Actions: FC<PartProps> = ({ className, children }) => (
  <div className={cn("flex items-center gap-2", className)}>{children}</div>
);

interface MenuButtonProps {
  label?: string;
  icon?: ReactNode;
  className?: string;
}

/** Renders only in the `mobile` layout, where it opens the `Nav` drawer. */
const MenuButton: FC<MenuButtonProps> = ({ label = "Abrir menu", icon = <Menu />, className }) => {
  const { layout, menuOpen, setMenuOpen } = useHeader();

  if (layout !== "mobile") return null;

  return (
    <button
      type="button"
      onClick={() => setMenuOpen(true)}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={menuOpen}
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center rounded-full text-fg-on-dark transition-colors hover:bg-nav-bg-hover [&>svg]:h-6 [&>svg]:w-6",
        className,
      )}
    >
      {icon}
    </button>
  );
};

Root.displayName = "Header";
Bar.displayName = "Header.Bar";
Brand.displayName = "Header.Brand";
Nav.displayName = "Header.Nav";
Item.displayName = "Header.Item";
Actions.displayName = "Header.Actions";
MenuButton.displayName = "Header.MenuButton";

export const Header = Object.assign(Root, { Bar, Brand, Nav, Item, Actions, MenuButton });
